import { styled } from '@mui/material/styles';

import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';
import simulation from 'assets/industries/simulation.svg';

import { breakpoints, devices } from 'styles/breakpoints';
import { colors } from 'styles';

interface IndustryUseCasesProps {
  itemQuantity: number;
}

interface IndustryAccessMoreButtonProps {
  isExpanded: boolean;
}

export const IndustryHeroWrapper = styled(Box)`
  position: relative;
  width: 100%;
  background-color: ${colors.mainDark};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 400px;

  @media (${devices.sm}) {
    min-height: 800px;
  }
`;

export const IndustryHeroOverlay = styled(Box)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: linear-gradient(180deg, rgba(11, 10, 8, 0) 0%, rgba(11, 10, 8, 0.4) 40%, #0B0A08 68%, #0B0A08 100%);
  z-index: 1;
`;

export const IndustryMainWrapper = styled(Box)`
  padding: 80px 32px 146px;
  position: relative;
  z-index: 3;
  width: 100%;
`;

export const IndustryMain = styled(Box)`
  display: flex;
  gap: 24px;
  flex-direction: column;
  margin: 0 auto;
  max-width: 1200px;
  text-align: center;

  @media (${devices.sm}) {
    flex-direction: row;
    text-align: left;
  }

  @media (${devices.lg}) {
    gap: 80px;
  }
`;
// this
export const IndustryMainVideo = styled('video')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

export const IndustryMainImage = styled('img')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

export const IndustryMainTitle = styled(Typography)`
  color: ${colors.white};
  font-size: 40px;
  font-weight: 700;
  line-height: 120%;
  white-space: pre-line;

  @media (${devices.sm}) {
    flex: 1;
  }

  @media (${devices.lg}) {
    font-size: 56px;
  }
`;

export const IndustryMainDesc = styled('div')`
  p {
    color: ${colors.white};
    font-size: 18px;
    line-height: 150%;
    margin: 0 0 32px;
  }

  @media (${devices.sm}) {
    flex: 1;

    p {
      margin: 0 0 24px;
    }
  }
`;

export const IndustryMainButtonWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (${devices.sm}) {
    flex-direction: row;
  }

  button {
    @media (max-width: ${breakpoints.sm}px) {
      width: 100%;
      margin: 0 auto;
    }
  }
`;

export const IndustryTopicWrapper = styled('div')`
  padding: 70px 16px;

  @media (${devices.sm}) {
    padding: 70px 24px;
  }

  @media (${devices.lg}) {
    margin: 0 auto;
    max-width: 1248px;
  }
`;

export const IndustryTopic = styled('div')`
  padding: 70px 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 80px;
  color: ${colors.mainDark};

  &:first-of-type {
    padding-top: 42px;
  }

  &:last-of-type {
    padding-bottom: 42px;
  }

  @media (${devices.sm}) {
    flex-direction: row;
    gap: 40px;

    &:nth-of-type(even) {
      flex-direction: row-reverse;
    }

    @media (${devices.lg}) {
      gap: 80px;
    }
  }
`;

export const IndustryTopicTextWrapper = styled('div')`
  @media (${devices.sm}) {
    flex: 1;
  }
`;

export const IndustryTopicImageWrapper = styled('div')`
  img {
    display: block;
    width: 100%;
    height: 300px;
    object-fit: cover;
    border-radius: 16px;

    @media (${devices.sm}) {
      height: 690px;
    }

    @media (${devices.lg}) {
      height: 600px;
    }
  }

  @media (${devices.sm}) {
    flex: 1;
  }
`;

export const IndustryTopicTitle = styled(Typography)`
  font-size: 32px;
  font-weight: 700;
  line-height: 130%;
  margin: 0;

  @media (${devices.lg}) {
    font-size: 36px;
    line-height: 120%;
  }
`;

export const IndustryTopicSubTitle = styled(Typography)`
  font-size: 18px;
  line-height: 150%;
  margin: 24px 0 32px;
`;

export const IndustryTopicList = styled('ul')`
  margin: 8px 0;
  padding: 0;
  list-style-type: none;
`;

export const IndustryTopicListItem = styled('li')`
  font-size: 16px;
  line-height: 150%;
  margin-bottom: 16px;
  position: relative;
  padding-left: 32px;

  &:before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background-image: url(${simulation});
    left: 0;
    top: 0;
    bottom: 0;
    margin: auto;
  }

  &:last-of-type {
    margin-bottom: 0;
  }

  @media (${devices.lg}) {
    font-size: 18px;
  }
`;

export const IndustryTopicListAlt = styled('ul')`
  margin: 8px 0;
  padding: 0;
  list-style-type: none;
  display: flex;
  gap: 24px;
`;

export const IndustryTopicListItemAlt = styled('li')`
  font-size: 16px;
  line-height: 150%;
  flex: 1;
`;

export const IndustryTopicListItemAltTitle = styled(Typography)`
  margin-bottom: 16px;
  font-size: 20px;
  font-weight: 700;
  line-height: 140%;
  white-space: pre-line;
`;

export const IndustryAccessWrapper = styled('div')`
  padding: 120px 16px 155px;
  background-color: ${colors.mainDark};
  position: relative;

  @media (${devices.sm}) {
    padding: 120px 34px 155px;
  }

  @media (${devices.lg}) {
    padding: 120px 16px 227px;
  }
`;

export const IndustryAccess = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 80px;
  flex-direction: column;
  margin: 0 auto;
  max-width: 1200px;
  color: ${colors.white};

  @media (${devices.sm}) {
    flex-direction: row;
  }
`;

export const IndustryAccessTitle = styled(Typography)`
  font-size: 36px;
  font-weight: 700;
  line-height: 120%;
  flex: 1;
  white-space: pre-line;
`;

export const IndustryAccessTextWrapper = styled('div')`
  font-size: 18px;
  line-height: 150%;
  flex: 1;
`;

export const IndustryAccessText = styled(Typography)`
  margin-top: 25px;
  font-size: 18px;
`;

export const IndustryAccessIcons = styled('div')`
  flex: 0 0 100%;
  display: flex;
  padding: 8px 0;
  justify-content: space-between;
  margin-top: -32px;

  @media (${devices.lg}) {
    margin-top: 0;
  }

  img {
    height: 48px;
    width: 48px;
  }
`;

export const IndustryAccessHiddenBlock = styled('div')`
  flex: 0 0 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;

  @media (${devices.lg}) {
    flex-direction: row;
    gap: 80px;
  }

  img {
    height: 48px;
    width: 48px;
  }
`;

export const IndustryAccessHiddenBlockItem = styled('div')`
  flex: 1;
`;

export const IndustryAccessHiddenBlockItemTitle = styled(Typography)`
  font-size: 24px;
  font-weight: 700;
  line-height: 140%;
  margin: 0 0 24px;
  white-space: pre-line;
`;

export const IndustryAccessHiddenBlockItemText = styled(Typography)`
  font-size: 18px;
  line-height: 150%;
`;

export const IndustryAccessMoreButton = styled('div')<IndustryAccessMoreButtonProps>`
  margin-top: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 16px;
  user-select: none;
  justify-content: center;

  @media (${devices.sm}) {
    justify-content: start;
  }

  img {
    width: 24px;
    height: 24px;
    transition: transform 0.3s ease;
    transform: ${({ isExpanded }) => (isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;

export const IndustryUseCasesWrapper = styled('div')`
  padding: 112px 16px;
  color: ${colors.mainDark};

  @media (${devices.sm}) {
    padding: 112px 24px;
  }

  @media (${devices.lg}) {
    margin: 0 auto;
    max-width: 1360px;
  }
`;

export const IndustryUseCasesTop = styled('div')`
  gap: 80px;
  flex-direction: column;
  display: flex;
  justify-content: space-between;

  @media (${devices.sm}) {
    flex-direction: row;
  }
`;

export const IndustryUseCasesTitle = styled(Typography)`
  max-width: 560px;
  align-items: flex-end;
  font-size: 32px;
  font-weight: 700;
  line-height: 130%;
  flex: 1;

  @media (${devices.lg}) {
    font-size: 40px;
    line-height: 120%;
  }
`;

export const IndustryUseCasesSubTitle = styled(Typography)`
  max-width: 560px;
  font-size: 18px;
  line-height: 150%;
  flex: 1;
`;

export const IndustryUseCases = styled('div')<IndustryUseCasesProps>`
  display: grid;
  gap: 48px;
  margin-top: 80px;
  margin-bottom: 112px;
  grid-template-columns: 1fr;

  @media (${devices.lg}) {
    ${({ itemQuantity }) =>
      itemQuantity === 3
        ? `
          grid-template-columns: repeat(3, 1fr);
        `
        : itemQuantity === 4
        ? `
          grid-template-columns: repeat(2, 1fr);
        `
        : ''}
  }
`;

export const IndustryUseCasesItem = styled('div')`
  flex: 1;

  img {
    height: 142px;
    object-fit: cover;
    display: block;
    width: 100%;
    border-radius: 8px;
  }

  h5 {
    font-size: 24px;
    font-weight: 700;
    line-height: 140%;
    margin: 24px 0;
  }

  p {
    font-size: 16px;
    line-height: 150%;
  }
`;
